import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { User } from "../types";
import { getMe, getStoredToken, setAuthToken, signIn, signUp } from "../api/auth";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await getMe();
      setUser({
        id: data.user.id,
        email: data.user.email,
      });
    } catch {
      setAuthToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email: string, password: string) => {
    const { data } = await signIn(email, password);
    if (data.access_token) {
      setAuthToken(data.access_token);
      setUser(data.user);
    }
  };

  const register = async (email: string, password: string) => {
    const { data } = await signUp(email, password);
    if (data.access_token) {
      setAuthToken(data.access_token);
      setUser(data.user);
      return;
    }
    await login(email, password);
  };

  const logout = () => {
    setAuthToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
